// Login.tsx — tela de login.
//
// Comportamento:
//  - Se Amplify configurado e usuário NÃO autenticado: botão SSO corporativo
//    que dispara `signInWithRedirect()` (OIDC hosted UI Cognito → IdC SAML).
//  - Se já autenticado: redireciona para `location.state.from` (ou /).
//  - Em erro de bootstrap/redirect: exibe feedback inline acessível.
//
// Visual: Apple/Linear calm material login — simples, premium, responsivo.

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowRight, ShieldCheck, WarningCircle } from '@phosphor-icons/react';
import { signInWithRedirect } from 'aws-amplify/auth';

import teamLoginArchitectureUrl from '@/assets/team-login-architecture.jpg';
import { AuraLogo } from '@/components/brand';
import { Button } from '@/components/ui/Button';
import { useSession } from '@/lib/auth/useSession';
import { isAmplifyConfigured } from '@/lib/auth/configureAmplify';
import { cn } from '@/lib/utils/cn';

interface LocationState {
  from?: { pathname?: string };
}

const SUPPORT_EMAIL = 'suporte@weaura.tech';
const ACCESS_POLICY_SUBJECT = 'Política de acesso do Aura TEAM Portal';
const SUPPORT_SUBJECT = 'Acesso ao Aura TEAM Portal';

export function Login() {
  const { status } = useSession();
  const location = useLocation();
  const reduce = useReducedMotion();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';

  // Se vier autenticado (já logado em outro tab), redireciona
  useEffect(() => {
    if (status === 'authenticated') {
      // soft redirect via Navigate abaixo
    }
  }, [status]);

  if (status === 'authenticated') {
    return <Navigate to={from} replace />;
  }

  const handleLogin = async () => {
    setError(null);

    if (!isAmplifyConfigured()) {
      setError(
        'SSO corporativo não está configurado neste ambiente. Use o mock local ou revise as variáveis Amplify.',
      );
      return;
    }

    try {
      setIsRedirecting(true);
      await signInWithRedirect({ provider: { custom: 'AuraIdC' } });
    } catch {
      setIsRedirecting(false);
      setError(
        'Não foi possível iniciar o SSO agora. Tente novamente ou acione o suporte interno.',
      );
    }
  };

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center overflow-x-hidden bg-canvas px-4 py-8 text-ink sm:px-6 sm:py-12">
      <div className="absolute inset-0 -z-20 overflow-hidden" aria-hidden="true">
        <img
          src={teamLoginArchitectureUrl}
          alt=""
          className="h-full w-full scale-105 object-cover opacity-[0.08] blur-[2px] saturate-50 contrast-105"
          decoding="async"
          draggable={false}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_10%,rgba(124,158,224,0.22),transparent_34%),linear-gradient(145deg,rgba(248,248,246,0.98)_0%,rgba(245,245,242,0.94)_52%,rgba(236,239,247,0.88)_100%)]" />
        <div className="absolute left-1/2 top-1/2 h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-ink/[0.035]" />
        <div className="absolute left-1/2 top-1/2 h-[27rem] w-[27rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-ink/[0.035]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-surface-muted/55 to-transparent" />
      </div>

      <motion.section
        className="w-[calc(100vw-2rem)] max-w-[26rem] sm:w-full"
        initial={reduce ? false : { opacity: 0, y: 10, scale: 0.985 }}
        animate={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
        aria-labelledby="login-title"
      >
        <div className="mb-4 flex flex-wrap items-center justify-center gap-2 text-center text-[0.68rem] font-medium uppercase tracking-[0.16em] text-muted sm:mb-5 sm:text-[0.72rem] sm:tracking-[0.2em]">
          <span>Portal interno</span>
          <span className="h-1 w-1 rounded-full bg-border" aria-hidden="true" />
          <span>SSO corporativo</span>
        </div>

        <div className="relative w-full max-w-full overflow-hidden rounded-[1.65rem] border border-ink/[0.07] bg-white/[0.72] px-4 py-8 text-center shadow-[0_24px_70px_-46px_rgba(6,11,58,0.42),0_1px_0_rgba(255,255,255,0.92)_inset] backdrop-blur-2xl sm:px-8 sm:py-9">
          <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/90 to-transparent" />

          <div className="flex flex-col items-center gap-7">
            <AuraLogo variant="navy" size="lg" ariaLabel="Aura" />

            <div className="flex w-full flex-col items-center gap-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-ink/[0.08] bg-white/70 px-3 py-1 text-xs font-medium text-muted shadow-[0_8px_24px_-18px_rgba(6,11,58,0.35)]">
                <ShieldCheck size={14} weight="bold" className="text-signal" aria-hidden="true" />
                Acesso verificado
              </div>

              <div className="flex max-w-[21rem] flex-col gap-3">
                <h1
                  id="login-title"
                  className="text-balance text-[1.78rem] font-semibold leading-[1.08] tracking-[-0.035em] text-ink sm:text-[2rem]"
                >
                  Acesse o portal Aura
                </h1>
                <p className="text-pretty text-[0.95rem] leading-relaxed text-muted sm:text-base">
                  Use seu SSO corporativo para solicitar, aprovar e acompanhar acessos temporários à
                  AWS.
                </p>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleLogin}
                disabled={isRedirecting}
                aria-label={isRedirecting ? 'Redirecionando…' : 'Entrar com SSO corporativo'}
                className={cn(
                  'min-h-12 w-full justify-center rounded-xl px-5 text-[0.95rem] shadow-[0_14px_32px_-22px_rgba(6,11,58,0.7)]',
                  isRedirecting && 'cursor-wait',
                )}
              >
                {isRedirecting ? (
                  'Redirecionando…'
                ) : (
                  <>
                    <span className="sm:hidden">Entrar com SSO</span>
                    <span className="hidden sm:inline">Entrar com SSO corporativo</span>
                    <ArrowRight size={18} weight="bold" aria-hidden="true" />
                  </>
                )}
              </Button>

              {error ? (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-xl border border-error/20 bg-error/5 px-3 py-2 text-left text-sm leading-relaxed text-error-text"
                >
                  <WarningCircle
                    size={18}
                    weight="bold"
                    className="mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>{error}</span>
                </div>
              ) : null}
            </div>

            <p className="max-w-[20rem] text-sm leading-relaxed text-muted">
              Acesso temporário, auditável e revogável. Use apenas para finalidades autorizadas.
            </p>
          </div>
        </div>

        <footer className="mt-5 text-center" aria-label="Suporte do login">
          <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-muted">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(ACCESS_POLICY_SUBJECT)}`}
              className="rounded-sm underline-offset-4 transition-colors hover:text-ink hover:underline"
            >
              Política de acesso
            </a>
            <span className="text-border" aria-hidden="true">
              ·
            </span>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_SUBJECT)}`}
              className="rounded-sm underline-offset-4 transition-colors hover:text-ink hover:underline"
            >
              Suporte interno
            </a>
          </nav>
        </footer>
      </motion.section>
    </main>
  );
}
